import {
  Player,
  Question,
  Cue,
  CueTypes,
  Results,
  TurnInfo,
  FtlOption,
  FtlStreakState,
  FtlTurnInfo,
  FtlStreakTurnResult,
  FtlStreakResults,
  ReverseRoundStartPayload,
  ReverseRoundGuessPhasePayload,
  ReverseRoundResultsPayload,
  HerdStreakState,
  HerdTurnInfo,
  HerdTurnResult,
  HerdStreakResults,
  PicassoStartPayload,
  PicassoGuessPhasePayload,
  PicassoResultsPayload,
  StandardVersusResultsPayload,
  TurnMode,
} from "../../shared/interfaces/GameLogicTypes";
import { serverUrl } from "../../shared/runtime";

// ─── Constants ──────────────────────────────────────────────────────────────────

const PLAYER_NAMES = ["Alex", "Jordan", "Sam", "Riley", "Morgan", "Casey", "Taylor", "Quinn"];
const FTL_COLORS = ["#608ad6", "#e75151", "#92dc67", "#e17d47"];

const MOCK_QUESTIONS: Question[] = [
  { forTsar: "What is <h>your</h> go-to morning drink?", aboutTsar: "What is <h>{{tsar}}</h>'s go-to morning drink?" },
  { forTsar: "What would <h>you</h> take to a desert island?", aboutTsar: "What would <h>{{tsar}}</h> take to a desert island?" },
  { forTsar: "What is <h>your</h> guilty pleasure?", aboutTsar: "What is <h>{{tsar}}</h>'s guilty pleasure?" },
];

const MOCK_CUE_SETS: Cue[][] = [
  [
    { type: CueTypes.text, content: "Coffee" },
    { type: CueTypes.text, content: "Tea" },
    { type: CueTypes.text, content: "Sparkling Water" },
    { type: CueTypes.text, content: "Energy Drink" },
    { type: CueTypes.text, content: "Fruit Juice" },
  ],
  [
    { type: CueTypes.text, content: "Phone" },
    { type: CueTypes.text, content: "Sunscreen" },
    { type: CueTypes.text, content: "Knife" },
    { type: CueTypes.text, content: "Hammock" },
    { type: CueTypes.text, content: "Fishing Rod" },
  ],
  [
    { type: CueTypes.text, content: "Reality TV" },
    { type: CueTypes.text, content: "Fast Food" },
    { type: CueTypes.text, content: "Gossip" },
    { type: CueTypes.text, content: "Napping" },
    { type: CueTypes.text, content: "Social Media" },
  ],
  // Long real-DB answers — stress test for bar font sizing / wrapping
  [
    { type: CueTypes.text, content: "International Adoption - A child from a different country and culture." },
    { type: CueTypes.text, content: "Stole someone else's luggage, thinking it was their own at the airport" },
    { type: CueTypes.text, content: "Einen Stuhl, der dich überall hinteleportiert, aber nur 5 Meter weiter" },
    { type: CueTypes.text, content: "Trying to take a sticker off something and it leaving a sticky residue" },
    { type: CueTypes.text, content: "Fear of being judged or criticized for your personal beliefs or values" },
  ],
];

const FALLBACK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300">
  <rect width="300" height="300" fill="#fff"/>
  <circle cx="150" cy="120" r="60" fill="none" stroke="#000" stroke-width="3"/>
  <circle cx="130" cy="105" r="5" fill="#000"/>
  <circle cx="170" cy="105" r="5" fill="#000"/>
  <path d="M125 140 Q150 165 175 140" fill="none" stroke="#000" stroke-width="3"/>
  <line x1="150" y1="180" x2="150" y2="240" stroke="#000" stroke-width="3"/>
  <line x1="150" y1="200" x2="120" y2="220" stroke="#000" stroke-width="3"/>
  <line x1="150" y1="200" x2="180" y2="220" stroke="#000" stroke-width="3"/>
  <line x1="150" y1="240" x2="120" y2="270" stroke="#000" stroke-width="3"/>
  <line x1="150" y1="240" x2="180" y2="270" stroke="#000" stroke-width="3"/>
</svg>`;

// ─── Real Picasso Drawings Pool ────────────────────────────────────────────────

let drawingPool: string[] = [];
let poolLoading = false;

/** Fetch a fresh batch of random real Picasso drawings from the database */
export async function loadRealDrawings(count = 20): Promise<void> {
  if (poolLoading) return;
  poolLoading = true;
  try {
    const res = await fetch(serverUrl(`/api/debug/picasso-drawings?count=${count}`));
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) drawingPool = data;
    }
  } catch { /* fall back to FALLBACK_SVG */ }
  poolLoading = false;
}

/** Get a random drawing from the pool, or fallback if pool is empty */
function getRandomDrawing(): string {
  if (drawingPool.length === 0) return FALLBACK_SVG;
  return drawingPool[Math.floor(Math.random() * drawingPool.length)];
}

// ─── Player Generation ──────────────────────────────────────────────────────────

export function generatePlayerIds(count: number): string[] {
  return Array.from({ length: count }, (_, i) => `player-${i}`);
}

export function makeOnePlayer(index: number): Player {
  return {
    name: PLAYER_NAMES[index] || `Player ${index + 1}`,
    score: 0,
    joinTime: index + 1,
    remainingSkips: 2,
    config: {},
  };
}

export function generatePlayers(count: number): { [id: string]: Player } {
  const players: { [id: string]: Player } = {};
  for (let i = 0; i < count; i++) {
    const id = `player-${i}`;
    players[id] = {
      name: PLAYER_NAMES[i] || `Player ${i + 1}`,
      score: 0,
      joinTime: i + 1,
      remainingSkips: 2,
      config: {},
    };
  }
  return players;
}

// ─── Utility ────────────────────────────────────────────────────────────────────

function shuffleArray<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function randomSequence(): number[] {
  return shuffleArray([1, 2, 3, 4, 5]);
}

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/** Compute standard-mode score + swaps for a sequence vs czar's [1,2,3,4,5] */
function computeScoreAndSwaps(seq: number[]): { score: number; swaps: { score: number; swap: [number, number] }[] } {
  const target = [1, 2, 3, 4, 5];
  const work = [...seq];
  let totalCost = 0;
  const swaps: { score: number; swap: [number, number] }[] = [];
  for (let i = 0; i < work.length; i++) {
    if (work[i] !== target[i]) {
      const targetIdx = work.indexOf(target[i]);
      const dist = Math.abs(i - targetIdx);
      const cost = dist * dist * 100;
      totalCost += cost;
      swaps.push({ score: cost, swap: [i, targetIdx] });
      [work[i], work[targetIdx]] = [work[targetIdx], work[i]];
    }
  }
  return { score: Math.max(0, 2000 - totalCost), swaps };
}

// ─── Standard Mode Builders ─────────────────────────────────────────────────────

export function getQuestion(index?: number): Question {
  return MOCK_QUESTIONS[index ?? Math.floor(Math.random() * MOCK_QUESTIONS.length)];
}

export function getCues(index?: number): Cue[] {
  return [...MOCK_CUE_SETS[index ?? Math.floor(Math.random() * MOCK_CUE_SETS.length)]];
}

export function buildTurnInfo(opts: {
  czarId: string;
  timer: number;
  roundLimit: number;
  questionIndex?: number;
  cueIndex?: number;
}): TurnInfo {
  const cues = getCues(opts.cueIndex);
  return {
    tsarId: opts.czarId,
    tsarJoinTime: 1,
    cues,
    question: getQuestion(opts.questionIndex),
    timer: opts.timer,
    startTime: Date.now(),
    submissions: {},
    results: {},
    roundLimit: opts.roundLimit,
    turnMode: TurnMode.STANDARD,
  };
}

export function buildStandardResults(
  players: { [id: string]: Player },
  czarId: string,
  cues: Cue[]
): Results {
  const results: Results = {};
  const czarSeq = [1, 2, 3, 4, 5]; // czar always has the "correct" order
  for (const [id, player] of Object.entries(players)) {
    if (id === czarId) {
      results[id] = {
        playerInfo: player,
        playerTurnResults: { sequence: czarSeq, swaps: [], points: 0 },
      };
    } else {
      const seq = randomSequence();
      const { score: points, swaps } = computeScoreAndSwaps(seq);
      results[id] = {
        playerInfo: player,
        playerTurnResults: { sequence: seq, swaps, points },
      };
    }
  }
  return results;
}

// ─── Standard-Versus Builders ────────────────────────────────────────────────────

export function buildStandardVersusTurnInfo(opts: {
  timer: number;
  roundLimit: number;
  cueIndex?: number;
}): TurnInfo {
  return {
    ...buildTurnInfo({ ...opts, czarId: "" }),
    tsarId: "",
    turnMode: TurnMode.STANDARD_VERSUS,
  };
}

export function buildStandardVersusResults(
  players: { [id: string]: Player },
  cues: Cue[]
): StandardVersusResultsPayload {
  const groupRanking = [1, 2, 3, 4, 5]; // the aggregate "friend group" ranking
  const results: Results = {};
  for (const [id, player] of Object.entries(players)) {
    const seq = randomSequence();
    const { score: points, swaps } = computeScoreAndSwaps(seq);
    results[id] = { playerInfo: player, playerTurnResults: { sequence: seq, swaps, points } };
  }
  return { groupRanking, cues, question: getQuestion(0), results };
}

// ─── FTL Mode Builders ──────────────────────────────────────────────────────────

function buildFtlOptions(count: number, cues?: Cue[]): FtlOption[] {
  const sourceCues = cues ?? getCues(0);
  return Array.from({ length: count }, (_, i) => ({
    index: i + 1,
    cue: sourceCues[i] ?? { type: CueTypes.text as CueTypes.text, content: `Option ${i + 1}` },
    color: FTL_COLORS[i] || "#888",
  }));
}

export function buildFtlStreakState(opts: {
  czarId: string;
  playerIds: string[];
  timer: number;
  optionCount?: number;
}): FtlStreakState {
  const nonCzar = opts.playerIds.filter((id) => id !== opts.czarId);
  const optionCount = opts.optionCount ?? 2;
  return {
    streakId: `debug-streak-${Date.now()}`,
    czarId: opts.czarId,
    streakIndex: 0,
    alivePlayers: nonCzar,
    deadPlayers: [],
    options: buildFtlOptions(optionCount),
    czarChoice: null,
    multiplier: 1,
    question: getQuestion(),
    timer: opts.timer,
    startTime: Date.now(),
    totalNonCzar: nonCzar.length,
  };
}

export function buildFtlTurnInfo(opts: {
  streakId: string;
  czarId: string;
  streakIndex: number;
  alivePlayers: string[];
  deadPlayers: string[];
  timer: number;
  multiplier: number;
  optionCount: number;
}): FtlTurnInfo {
  return {
    options: buildFtlOptions(opts.optionCount),
    question: getQuestion(),
    timer: opts.timer,
    startTime: Date.now(),
    streakIndex: opts.streakIndex,
    alivePlayers: opts.alivePlayers,
    deadPlayers: opts.deadPlayers,
    multiplier: opts.multiplier,
    czarId: opts.czarId,
    streakId: opts.streakId,
  };
}

export function buildFtlTurnResult(opts: {
  streakIndex: number;
  czarChoice: number;
  multiplier: number;
  alivePlayers: string[];
  eliminateIds?: string[];
  czarId: string;
}): FtlStreakTurnResult {
  const eliminations = opts.eliminateIds ??
    (opts.alivePlayers.length > 1 ? [pickRandom(opts.alivePlayers)] : []);
  const survivors = opts.alivePlayers.filter((id) => !eliminations.includes(id));
  const pointsAwarded: { [id: string]: number } = {};
  survivors.forEach((id) => { pointsAwarded[id] = 500 * opts.multiplier; });
  pointsAwarded[opts.czarId] = 250 * (survivors.length + eliminations.length);
  return {
    streakIndex: opts.streakIndex,
    czarChoice: opts.czarChoice,
    multiplier: opts.multiplier,
    eliminations,
    survivors,
    pointsAwarded,
  };
}

export function buildFtlStreakResults(
  turnResults: FtlStreakTurnResult[],
  czarId: string,
  streakId: string
): FtlStreakResults {
  const totals: { [id: string]: number } = {};
  for (const tr of turnResults) {
    for (const [id, pts] of Object.entries(tr.pointsAwarded)) {
      totals[id] = (totals[id] || 0) + pts;
    }
  }
  return { streakId, czarId, turnResults, totalPointsByPlayer: totals };
}

// ─── Herd Mode Builders ─────────────────────────────────────────────────────────

export function buildHerdStreakState(opts: {
  playerIds: string[];
  timer: number;
}): HerdStreakState {
  const cues = getCues();
  return {
    streakId: `debug-herd-${Date.now()}`,
    streakIndex: 0,
    alivePlayers: [...opts.playerIds],
    deadPlayers: [],
    options: cues.slice(0, 3),
    question: getQuestion(),
    timer: opts.timer,
    startTime: Date.now(),
  };
}

export function buildHerdTurnInfo(opts: {
  streakId: string;
  streakIndex: number;
  alivePlayers: string[];
  deadPlayers: string[];
  timer: number;
}): HerdTurnInfo {
  const cues = getCues();
  return {
    streakId: opts.streakId,
    streakIndex: opts.streakIndex,
    options: cues.slice(0, 3),
    alivePlayers: opts.alivePlayers,
    deadPlayers: opts.deadPlayers,
    timer: opts.timer,
    startTime: Date.now(),
  };
}

export function buildHerdTurnResult(opts: {
  streakIndex: number;
  alivePlayers: string[];
  wasSplit?: boolean;
  eliminateIds?: string[];
}): HerdTurnResult {
  const wasSplit = opts.wasSplit ?? false;
  const eliminations = opts.eliminateIds ??
    (opts.alivePlayers.length > 1 && !wasSplit ? [pickRandom(opts.alivePlayers)] : []);
  const survivors = opts.alivePlayers.filter((id) => !eliminations.includes(id));
  const pointsAwarded: { [id: string]: number } = {};
  survivors.forEach((id) => { pointsAwarded[id] = 500; });
  const cues = getCues();
  return {
    streakIndex: opts.streakIndex,
    optionCounts: [2, 1, 0],
    eliminations,
    survivors,
    pointsAwarded,
    wasSplit,
    options: cues.slice(0, 3),
  };
}

export function buildHerdStreakResults(
  turnResults: HerdTurnResult[],
  streakId: string
): HerdStreakResults {
  const totals: { [id: string]: number } = {};
  for (const tr of turnResults) {
    for (const [id, pts] of Object.entries(tr.pointsAwarded)) {
      totals[id] = (totals[id] || 0) + pts;
    }
  }
  return { streakId, turnResults, totalPointsByPlayer: totals };
}

// ─── Reverse Round Builders ─────────────────────────────────────────────────────

export function buildRrStartPayload(timer: number): ReverseRoundStartPayload {
  return {
    question: getQuestion(),
    cues: getCues(),
    timer,
    startTime: Date.now(),
  };
}

export function buildRrGuessPhasePayload(
  players: { [id: string]: Player },
  viewAsId: string
): ReverseRoundGuessPhasePayload {
  const otherIds = Object.keys(players).filter((id) => id !== viewAsId);
  const originId = otherIds.length > 0 ? pickRandom(otherIds) : null;
  const options = otherIds.slice(0, 3).map((id) => ({ id, name: players[id].name }));
  if (options.length < 3) {
    options.push({ id: "__not_one_of_us__", name: "Not one of us" });
  }
  return {
    shownSequence: randomSequence(),
    shownCues: getCues(),
    originPlayerId: originId,
    isRandom: false,
    options,
    optionCount: options.length,
    highRollAvailable: true,
    timer: 50,
    startTime: Date.now(),
  };
}

export function buildRrResults(
  players: { [id: string]: Player }
): ReverseRoundResultsPayload {
  const playerIds = Object.keys(players);
  const sequences = playerIds.slice(0, 3).map((id) => ({
    sequence: randomSequence(),
    submittedBy: id,
    submitterName: players[id].name,
    isRandom: false,
    shownTo: [pickRandom(playerIds.filter((p) => p !== id))],
    guesses: [{
      playerId: pickRandom(playerIds.filter((p) => p !== id)),
      playerName: players[pickRandom(playerIds.filter((p) => p !== id))]?.name ?? "Unknown",
      guessedId: id,
      guessedName: null,
      correct: Math.random() > 0.5,
      points: Math.random() > 0.5 ? 1500 : 0,
    }],
  }));
  const playerPoints: { [id: string]: number } = {};
  const ownerBonuses: { [id: string]: number } = {};
  playerIds.forEach((id) => { playerPoints[id] = Math.floor(Math.random() * 2) * 1500; });
  return { sequences, playerPoints, ownerBonuses };
}

// ─── Picasso Builders ───────────────────────────────────────────────────────────

export function buildPicassoStartPayload(timer: number): PicassoStartPayload {
  return {
    question: { forTsar: "Draw <h>your</h> happy place", aboutTsar: "Draw <h>{{tsar}}</h>'s happy place" },
    timer,
    startTime: Date.now(),
  };
}

export function buildPicassoGuessPhasePayload(timer: number): PicassoGuessPhasePayload {
  return {
    drawingA: getRandomDrawing(),
    drawingB: getRandomDrawing(),
    targetPlayerName: "Jordan",
    timer,
    startTime: Date.now(),
  };
}

export function buildPicassoResults(
  players: { [id: string]: Player }
): PicassoResultsPayload {
  const playerIds = Object.keys(players);
  const drawings = playerIds.slice(0, 4).map((id, i) => ({
    playerId: id,
    playerName: players[id].name,
    drawingSvg: getRandomDrawing(),
    correctGuessCount: Math.floor(Math.random() * 4),
    pointsEarned: Math.floor(Math.random() * 3) * 500,
    pointBreakdown: "Debug points",
    ...(i === 3 ? { isImposter: true, imposterTarget: players[playerIds[1]]?.name } : {}),
  }));
  const challengeResults = [
    {
      type: "imposter" as const,
      drawingA: getRandomDrawing(),
      drawingB: getRandomDrawing(),
      realLabel: "A" as const,
      drawerAName: players[playerIds[1]]?.name ?? "Unknown",
      drawerBName: players[playerIds[3]]?.name ?? "Unknown",
      targetPlayerName: players[playerIds[1]]?.name ?? "Unknown",
      imposterName: players[playerIds[3]]?.name ?? "Unknown",
      voters: playerIds.slice(0, 2).map((id) => ({
        playerName: players[id].name,
        vote: (Math.random() > 0.5 ? "A" : "B") as "A" | "B",
        correct: Math.random() > 0.5,
        points: Math.random() > 0.5 ? 400 : 0,
      })),
    },
  ];
  const playerPoints: { [id: string]: number } = {};
  playerIds.forEach((id) => { playerPoints[id] = Math.floor(Math.random() * 5) * 500; });
  return {
    drawings,
    challengeResults,
    playerPoints,
    imposterResults: playerIds.length >= 4 ? [{
      imposterName: players[playerIds[3]]?.name ?? "Unknown",
      targetName: players[playerIds[1]]?.name ?? "Unknown",
      accepted: true,
      fooledCount: 2,
      imposterPoints: 1600,
    }] : [],
  };
}
